// src/pages/CategoryPage.js
import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Button,
  CircularProgress,
  Box,
  List,
  ListItem,
  TextField,
  Paper,
  IconButton
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';

const CategoryPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { currentUser } = useAuth();

  useEffect(() => {
    if (!location.state || !location.state.projectID) {
      navigate('/projects');
    }
  }, [location.state, navigate]);

  const {
    updatedCategoryList = [],
    projectID,
    codeName,
    dataCount,
    question,
    remainingQuota,
    paramaters_online,
    selectedTags = []
  } = location.state || {};

  const [categories, setCategories] = useState([]);
  const [saving, setSaving] = useState(false);
  const [regenLoading, setRegenLoading] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');

  useEffect(() => {
    if (updatedCategoryList && updatedCategoryList.length > 0) {
      const transformed = updatedCategoryList.map(item => ({
        categoryName: item.category_name || item.categoryName || '',
        categoryCode: item.category_code || item.categoryCode || ''
      }));
      setCategories(transformed);
    }
  }, [updatedCategoryList]);

  const addCategory = () => {
    setCategories(prev => [...prev, { categoryName: '', categoryCode: '' }]);
  };

  const deleteCategory = (index) => {
    setCategories(prev => prev.filter((_, i) => i !== index));
  };

  const updateCategoryName = (index, value) => {
    setCategories(prev => {
      const newCategories = [...prev];
      newCategories[index].categoryName = value;
      return newCategories;
    });
  };

  const regenrateThemes = (themeSize) => {
    setRegenLoading(true);
    const formData = new FormData();
    formData.append('projectID', projectID);
    formData.append('codeName', codeName);
    formData.append('themeSize', themeSize);
    formData.append('prompt', aiPrompt);
    fetch("https://codini-project-1-regenerate-gt-795950092325.europe-west3.run.app", {
      method: "POST",
      body: formData
    })
      .then(response => response.json())
      .then(data => {
        setRegenLoading(false);
        if (data && data.updated_category_list) {
          const transformed = data.updated_category_list.map(item => ({
            categoryName: item.category_name || item.categoryName || '',
            categoryCode: item.category_code || item.categoryCode || ''
          }));
          setCategories(transformed);
        }
      })
      .catch(error => {
        console.error(t('codebook.regenError'), error);
        setRegenLoading(false);
      });
  };

  const saveCategoriesHandler = () => {
    setSaving(true);
    let maxCode = 0;
    categories.forEach(cat => {
      const codeNum = parseInt(cat.categoryCode, 10);
      if (!isNaN(codeNum)) {
        maxCode = Math.max(maxCode, codeNum);
      }
    });
    const updatedList = categories.map(cat => {
      let { categoryName, categoryCode } = cat;
      if (!categoryCode) {
        maxCode += 1;
        categoryCode = maxCode.toString();
      }
      return { categoryName, categoryCode };
    });
    if (updatedList.length > 200) {
      alert(t('codebook.exceedCategories'));
      setSaving(false);
      return;
    }
    if (Number(dataCount) > Number(remainingQuota)) {
      alert(t('codebook.quotaExceeded'));
      setSaving(false);
      navigate(-1);
      return;
    }
    const formData = new FormData();
    formData.append("projectID", projectID);
    formData.append("userEmail", currentUser.email);
    formData.append("updatedCategoryList", JSON.stringify(updatedList));
    formData.append("tags", JSON.stringify(selectedTags));
    formData.append("codeName", codeName);
    const fetchPromise = fetch("https://codini-project-2-gt-795950092325.europe-west3.run.app", {
      method: "POST",
      body: formData
    }).then(response => response.json());
    const timeoutPromise = new Promise(resolve =>
      setTimeout(() => resolve('timeout'), 7000)
    );
    Promise.race([fetchPromise, timeoutPromise])
      .then(() => {
        setSaving(false);
        navigate(-1);
      })
      .catch(error => {
        console.error(t('codebook.saveError'), error);
        setSaving(false);
        navigate(-1);
      });
  };

  return (
    <Box sx={{ backgroundColor: '#f4f4f4', minHeight: '100vh', padding: '20px' }}>
      <Container sx={{ mt: 1, backgroundColor: '#fff', borderRadius: 2, padding: '3px' }}>
        {/* Üst Başlık ve Proje Bilgileri */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mt: 2, mb: 2 }}>
          <Button variant="outlined" onClick={() => navigate('/ProjectsPage')}>
            {t('cancel')}
          </Button>
        </Box>
        <Paper elevation={1} sx={{ p: 1, mb: 2, background:'#fed9b7' }}>
          <Typography variant="h5" component="h1" gutterBottom>
            {t('codebook.codebook')}
          </Typography>
          <Typography variant="body1">
            {t('question')}: {question}
          </Typography>
          <Typography variant="body1">
            {t('dataCount')}: {dataCount}
          </Typography>
        </Paper>
        {/* Temaları Yenile Bölümü */}
        {paramaters_online !== "frommaster" && (
          <Paper elevation={1} sx={{ p: 1, mt: 2, background: '#e9d8a6', textAlign: 'center' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
              {t('codebook.regenerateThemes')}
            </Typography>
            <TextField
              label={t('codebook.aiPrompt')}
              variant="outlined"
              value={aiPrompt}
              onChange={(e) => setAiPrompt(e.target.value)}
              sx={{ width: '75%', mb: 2, backgroundColor: '#fff', ml: 2, mr: 2 }}
              placeholder={t('codebook.aiPromptPlaceholder')}
            />
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
              <Button
                variant="contained"
                sx={{ backgroundColor: '#34a0a4', '&:hover': { backgroundColor: '#9bf6ff' } }}
                onClick={() => regenrateThemes("short")}
              >
                {t('codebook.moreSummarized')}
              </Button>
              <Button
                variant="contained"
                sx={{ backgroundColor: '#1a759f', '&:hover': { backgroundColor: '#9bf6ff' } }}
                onClick={() => regenrateThemes("repeat")}
              >
                {t('codebook.refresh')}
              </Button>
              <Button
                variant="contained"
                sx={{ backgroundColor: '#184e77', '&:hover': { backgroundColor: '#9bf6ff' } }}
                onClick={() => regenrateThemes("large")}
              >
                {t('codebook.moreDetailed')}
              </Button>
            </Box>
          </Paper>
        )}
        {/* Kategori Listesi */}
        <List sx={{ width: '100%', mb: 1 }}>
          {categories.length > 0 ? (
            categories.map((cat, index) => (
              <ListItem
                key={index}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  mb: 0.5,
                  borderBottom: '1px solid #eee',
                  py: 0.5,
                }}
              >
                <Typography variant="body2" sx={{ minWidth: 60, mr: 0.5 }}>
                  {cat.categoryCode || ' '}
                </Typography>
                <TextField
                  value={cat.categoryName}
                  placeholder={t('codebook.category')}
                  onChange={(e) => updateCategoryName(index, e.target.value)}
                  size="small"
                  sx={{ flexGrow: 1, mr: 0.5, background: "#fff" }}
                />
                <IconButton
                  size="small"
                  onClick={() => deleteCategory(index)}
                  aria-label={t('delete')}
                >
                  <DeleteIcon />
                </IconButton>
              </ListItem>
            ))
          ) : (
            <Typography variant="body1" align="center">
              {t('codebook.noCategory')}
            </Typography>
          )}
        </List>
        {/* Kategori Ekle Butonu */}
        <Box sx={{ mb: 2, textAlign: 'center' }}>
          <Button variant="contained" onClick={addCategory}>
            {t('codebook.addCategory')}
          </Button>
        </Box>
        {/* Kaydet Butonu */}
        <Box sx={{ mt: 3, mb: 2, display: 'flex', justifyContent: 'space-between' }}>
          <Button variant="contained" color="success" onClick={saveCategoriesHandler}>
            {t('codebook.startCoding')}
          </Button>
        </Box>
        {(saving || regenLoading) && (
          <Box
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0,0,0,0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <CircularProgress />
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default CategoryPage;